import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { EntityType } from 'UI/constants/entityTypes';
import { REQUIRED_VALIDATION } from 'UI/utils';

import { DateTimePickers } from '../shared';

import {
  ENTITY_OPTIONS_TO_REACH_OUT,
  FORM_FIELDS_MAP,
  REACH_OUT_AUTOCOMPLETE_PROPS_BY_ENTITY
} from './OtherTask.constants';
import { useStyles } from './OtherTask.styles';

const { form: COPIES } = strings.dailyPlan.drawer;

export const OtherTask = ({ entityType = null, isMultiple = false, disableTime = false }) => {
  const classes = useStyles();
  const { errors, register, unregister, watch, setValue, getValues } = useFormContext();
  const formValues = watch();
  const reachToEntity = formValues[FORM_FIELDS_MAP.ReachToEntity.key];
  const taskId = formValues[FORM_FIELDS_MAP.TaskId.key];

  useEffect(() => {
    if (!isMultiple) {
      register(FORM_FIELDS_MAP.ReachToEntity.key);
      register(FORM_FIELDS_MAP.ReachToName.key, {
        validate: value => {
          const values = getValues();
          return (
            (values[FORM_FIELDS_MAP.ReachToEntity.key] ? !!value : true) || 'This field is required'
          );
        }
      });
    }

    return () => {
      if (!isMultiple) {
        unregister(FORM_FIELDS_MAP.ReachToEntity.key);
        unregister(FORM_FIELDS_MAP.ReachToName.key);
      }
    };
  }, [getValues, isMultiple, register, unregister]);

  const handleAutocompleteChange = (name, value) => {
    if (value === null) {
      setValue(FORM_FIELDS_MAP.ReachToEntity.key, null);
    }
    setValue(name, value, true);
  };

  const handleRadioChange = event => {
    const { value } = event.target;
    setValue(FORM_FIELDS_MAP.ReachToName.key, null);
    setValue(FORM_FIELDS_MAP.ReachToEntity.key, value, true);
  };

  const handleTextBoxChange = (name, value) => {
    setValue(name, value);
  };

  const handleClearEntityToReach = () => {
    setValue(FORM_FIELDS_MAP.ReachToEntity.key, null);
    setValue(FORM_FIELDS_MAP.ReachToName.key, null);
  };

  return (
    <Grid container spacing={2}>
      <Grid container item spacing={2} alignItems="center">
        <Grid item sm={12}>
          <TextBox
            name={FORM_FIELDS_MAP.OtherTask.key}
            error={!!errors[FORM_FIELDS_MAP.OtherTask.key]}
            errorText={errors[FORM_FIELDS_MAP.OtherTask.key]?.message}
            inputRef={register(REQUIRED_VALIDATION)}
            label={COPIES.placeholders.otherTask}
            placeholder={COPIES.placeholders.otherTask}
            onChange={handleTextBoxChange}
            value={formValues[FORM_FIELDS_MAP.OtherTask.key] || ''}
          />
        </Grid>

        <Grid container item spacing={2}>
          <DateTimePickers disableTime={disableTime} />
        </Grid>
      </Grid>

      <When condition={!isMultiple}>
        <Grid item xs={12}>
          <Typography variant="body1" className={classes.fieldLabel}>
            <b>{COPIES.labels.taskDetails}</b>
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="body1" color="textSecondary">
            {COPIES.labels.reachOut}
          </Typography>
          <FormControl
            component="fieldset"
            error={!!errors[FORM_FIELDS_MAP.ReachToEntity.key]}
            disabled={!!entityType && entityType !== EntityType.Joborder}
          >
            <RadioGroup
              aria-label="position"
              name={FORM_FIELDS_MAP.ReachToEntity.key}
              value={formValues[FORM_FIELDS_MAP.ReachToEntity.key] ?? null}
              onChange={handleRadioChange}
              row
            >
              {ENTITY_OPTIONS_TO_REACH_OUT.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={<Radio color="primary" />}
                  label={option.label}
                  labelPlacement="end"
                  value={option.value}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{errors[FORM_FIELDS_MAP.ReachToEntity.key]?.message}</FormHelperText>
          </FormControl>
          <When condition={!!formValues[FORM_FIELDS_MAP.ReachToEntity.key]}>
            <Button color="primary" onClick={handleClearEntityToReach}>
              Clear
            </Button>
          </When>
        </Grid>
      </When>

      <When condition={!!reachToEntity && !isMultiple}>
        <Grid item xs={12}>
          <AutocompleteSelect
            name={FORM_FIELDS_MAP.ReachToName.key}
            error={!!errors[FORM_FIELDS_MAP.ReachToName.key]}
            errorText={errors[FORM_FIELDS_MAP.ReachToName.key]?.message}
            onSelect={handleAutocompleteChange}
            selectedValue={formValues[FORM_FIELDS_MAP.ReachToName.key] ?? null}
            typeahead
            typeaheadLimit={50}
            disabled={!!entityType && entityType !== EntityType.Joborder}
            {...REACH_OUT_AUTOCOMPLETE_PROPS_BY_ENTITY[reachToEntity]}
          />
        </Grid>
      </When>

      <When condition={!taskId}>
        <Grid item xs={12}>
          <Typography variant="body1">
            <b>{COPIES.labels.comments}</b>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextBox
            label={COPIES.placeholders.comments}
            placeholder={COPIES.placeholders.comments}
            name={FORM_FIELDS_MAP.Comments.key}
            onChange={handleTextBoxChange}
            value={formValues[FORM_FIELDS_MAP.Comments.key]}
            inputRef={register()}
            multiline
          />
        </Grid>
      </When>
    </Grid>
  );
};
